import { put, post } from '../../../shared/api/apiService';
import { useApiEssentials } from '../../../shared/hooks/useApiEssentials';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { keepPreviousData } from '@tanstack/react-query';
import { RegistrationRequest, RegistrationRequestStatus } from '../types';
import { buildSupportFilter } from './useSupportRequests';

export interface UseRegistrationOptions {
  page?: number;
  filters?: Array<{
    field: string;
    value: string;
  }>;
  limit?: number;
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
}

interface RegistrationRequestPayload {
  page: number;
  filter: ReturnType<typeof buildSupportFilter>;
  limit: number;
  sort: Array<{
    field: string;
    direction: string;
  }>;
}

export function useRegistration({
  limit = 10,
  page = 0,
  filters = [],
  sortField = 'status',
  sortOrder = 'asc',
}: UseRegistrationOptions = {}) {
  const { getIdToken, apiUrl, isSignedIn } = useApiEssentials();

  return useQuery({
    queryKey: [
      'registration-requests',
      { limit, page, filters, sortField, sortOrder },
    ],
    queryFn: async () => {
      const idToken = await getIdToken();

      const payload: RegistrationRequestPayload = {
        page,
        filter: buildSupportFilter(filters),
        limit,
        sort: sortField
          ? [{ field: sortField, direction: sortOrder.toUpperCase() }]
          : [],
      };

      return await post<RegistrationRequest>(
        `/sign-up/page`,
        payload,
        apiUrl,
        idToken,
      );
    },
    enabled: isSignedIn,
    placeholderData: keepPreviousData,
    gcTime: 300000,
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: 5 * 60 * 1000,
  });
}

export function useUpdateUserRequestStatus() {
  const { getIdToken, apiUrl } = useApiEssentials();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      status,
    }: {
      userId: string;
      status: RegistrationRequestStatus;
    }) => {
      const idToken = await getIdToken();
      return await put<RegistrationRequest>(
        `/sign-up/${userId}/status/${status}`,
        {},
        apiUrl,
        idToken,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['registration-requests'] });
      queryClient.invalidateQueries({ queryKey: ['request-new-count'] });
    },
  });
}
